import { isFulfillable } from './promo-fulfillment-utils';

const pipe = (...funcs) => (arg) => funcs.reduce((acc, cur) => cur(acc), arg);

// TODO: branch coverage hidden in tests. these are uncovered branches
const isValidBogoData = (promotion) => {
  return promotion.subExperienceTag === 'buyOneGetOne'
      && promotion.anchorItemList.length > 0;
};

// TODO: branch coverage hidden in tests. these are uncovered branches
const isValidMSBData = (promotion) => {
  const rewardTiers = promotion.reward?.tiers;
  return (Array.isArray(rewardTiers) && typeof rewardTiers[0] === 'object'
      && (rewardTiers[0].minPurchaseAmount || rewardTiers[0].minPurchaseQuantity)
      && (rewardTiers[0].rewardPercent
        || rewardTiers[0].rewardAmountPerOrder
        || rewardTiers[0].rewardAmountPerItem));
};

// TODO: branch coverage hidden in tests. these are uncovered branches
const isValidPromotionData = (promotion) => {
  if (!promotion
      || (typeof promotion.promotionId !== 'number' && !promotion.promoCode)
      || typeof promotion.experienceTag !== 'string'
      || !promotion.itemList?.[0]) {
    return false;
  }

  switch (promotion.experienceTag) {
    case 'BOGO':
      return isValidBogoData(promotion);
    case 'MSBONLINE':
      return isValidMSBData(promotion);
    default:
      return false;
  }
};

const groupItemsByBrandAndSortAlphabetically = ((products) => (
  products.sort(({ identifiers: item1 }, { identifiers: item2 }) => {
    const brand1 = item1?.brandName;
    const brand2 = item2?.brandName;
    const label1 = item1?.productLabel;
    const label2 = item2?.productLabel;

    if (!brand1 || !label1) return 1;
    if (!brand2 || !label2) return -1;

    if (brand1 < brand2) return -1;
    if (brand1 > brand2) return 1;

    if (label1 < label2) return -1;
    if (label1 > label2) return 1;

    return 0;
  })
));

const filterItems = (items) => items.filter((item) => (
  item?.identifiers
    && item.pricing
    && isFulfillable(item)
    && typeof item.itemId === 'string'
    && item.itemId.length === 9
    && !Number.isNaN(item.itemId)
));

const sortAnchorItemToTopOfList = ((items, anchorId) => items.sort((item1, item2) => {
  if (item1.itemId === anchorId) return -1;
  if (item2.itemId === anchorId) return 1;
  return 0;
}));

const collectItemsMap = (items) => {
  const itemsMap = new Map();
  const itemIds = [];
  items.forEach((item) => {
    itemIds.push(item.itemId);
    itemsMap.set(item.itemId, item);
  });

  return {
    itemIds,
    itemsMap,
  };
};

const getSanitizedItems = (items = [], anchorItem) => {
  return pipe(
    filterItems,
    groupItemsByBrandAndSortAlphabetically,
    (groupedItems) => sortAnchorItemToTopOfList(groupedItems, anchorItem.itemId),
    collectItemsMap
  )(items);
};

/**
 * if BOGO, anchor is NOT in itemList. check anchor fulfillable and at least one free item
 * if MSB, anchor IS in itemList. Check itemList has at least one non-anchor item
 * @param {Array} itemList
 * @returns {boolean} if itemList has minimum number of products
 */
const hasMinimumNumberOfProducts = (isBogo, isMsb, itemList, anchorItem) => {
  const anchorIsFulfillable = isFulfillable(anchorItem);
  return (isBogo && anchorIsFulfillable && itemList.length > 1)
      || (isMsb && anchorIsFulfillable && itemList.length > 1)
      || (isMsb && !anchorIsFulfillable && itemList.length > 0);
};

const getSubExperience = (promoData) => {
  if (promoData.experienceTag === 'BOGO') return promoData.subExperienceTag;
  // MSBONLINE
  const {
    minPurchaseQuantity, minPurchaseAmount, rewardPercent, rewardAmountPerItem, rewardAmountPerOrder
  } = promoData.reward.tiers[0];
  // BuyXorMoreGetYPercent
  if (minPurchaseQuantity && rewardPercent) return 'MSB1';
  // BuyXorMoreSaveYDollar
  if (minPurchaseQuantity && rewardAmountPerOrder) return 'MSB2';
  // BuyXorMoreSaveYDollarEach
  if (minPurchaseQuantity && rewardAmountPerItem) return 'MSB3';
  // BuyXDollarGetYPercent
  if (minPurchaseAmount && rewardPercent) return 'MSB4';
  // BuyXDollarGetYDollar
  if (minPurchaseAmount && rewardAmountPerOrder) return 'MSB5';
  // BuyXDollarGetYDollarEach
  // minPurchaseAmount && rewardAmountPerItem
  return 'MSB6';
};

const transformItemDataToPromoModel = (products, anchorItem) => {
  // we are omitting itemIds that are BOPIS/STH/etc unfulfillable but are in cart for BODFS... edge case
  const promotion = anchorItem.pricing.promotion;
  const isBogo = promotion.experienceTag === 'BOGO';
  const isMsb = promotion.experienceTag === 'MSBONLINE';

  let { itemIds, itemsMap } = getSanitizedItems(products, anchorItem);
  const itemCount = itemIds.length;
  const singleItemIdToDisplay = itemCount === 1 ? itemIds[0] : null;
  if (isBogo) itemIds = [anchorItem.itemId, ...itemIds];

  return {
    products: itemsMap,
    anchorItemImage: anchorItem.media?.image?.url || '',
    isBogo,
    isMsb,
    promoId: promotion.promotionId,
    allItemIds: itemIds,
    hasProducts: hasMinimumNumberOfProducts(isBogo, isMsb, itemIds, anchorItem),
    itemCount,
    singleItemIdToDisplay,
    experienceTag: promotion.experienceTag,
    subExperience: getSubExperience(promotion),
    minPurchaseAmount: promotion.reward?.tiers?.[0]?.minPurchaseAmount || 0,
    minPurchaseQuantity: promotion.reward?.tiers?.[0]?.minPurchaseQuantity || 0,
    rewardAmountPerItem: promotion.reward?.tiers?.[0]?.rewardAmountPerItem || 0,
    rewardAmountPerOrder: promotion.reward?.tiers?.[0]?.rewardAmountPerOrder || 0,
    rewardPercent: promotion.reward?.tiers?.[0]?.rewardPercent || 0,
  };
};

export {
  pipe,
  isValidPromotionData,
  transformItemDataToPromoModel
};
